import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogBody,
  DialogFooter,
  Button,
  Input,
  Textarea,
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
  SegmentedControl,
} from '@vnetwork/ui/primitives';
import { Field } from '@vnetwork/ui/forms';
import { useToast } from '@vnetwork/ui/feedback';
import {
  useCreateTicket,
  useTicketTypes,
  useTicketServices,
  type TicketPriority,
} from '@/api/support';
import { ticketTypeLabel } from '../shared';
import { trackSupportTicketCreate } from '@/lib/analytics-events';

const NO_SERVICE = 'none';

interface CreateTicketDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Called with the new ticket id once creation succeeds. */
  onCreated?: (ticketId: number) => void;
}

const PRIORITY_ITEMS: { value: TicketPriority; label: string }[] = [
  { value: 'low', label: 'Low' },
  { value: 'medium', label: 'Medium' },
  { value: 'high', label: 'High' },
];

export function CreateTicketDialog({ open, onOpenChange, onCreated }: CreateTicketDialogProps) {
  const { show } = useToast();
  const { data: ticketTypes = [], isLoading: typesLoading } = useTicketTypes();
  const { data: services = [], isLoading: servicesLoading } = useTicketServices();

  const [typeId, setTypeId] = useState('');
  const [priority, setPriority] = useState<TicketPriority>('medium');
  const [serviceId, setServiceId] = useState(NO_SERVICE);
  const [productName, setProductName] = useState('');
  const [content, setContent] = useState('');
  const [errors, setErrors] = useState<{ type?: string; content?: string }>({});

  const create = useCreateTicket({
    onSuccess: (ticket) => {
      trackSupportTicketCreate(priority, serviceId !== NO_SERVICE ? serviceId : undefined);
      show({ tone: 'success', title: 'Ticket created', description: `#${ticket.ticket_id}` });
      onOpenChange(false);
      onCreated?.(ticket.ticket_id);
    },
    onError: () => show({ tone: 'danger', title: 'Could not create ticket' }),
  });

  function reset() {
    setTypeId('');
    setPriority('medium');
    setServiceId(NO_SERVICE);
    setProductName('');
    setContent('');
    setErrors({});
  }

  function handleOpenChange(next: boolean) {
    if (!next) reset();
    onOpenChange(next);
  }

  function handleSubmit() {
    const nextErrors: typeof errors = {};
    if (!typeId) nextErrors.type = 'Select a ticket type.';
    if (!content.trim()) nextErrors.content = 'Describe your issue.';
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) return;

    create.mutate({
      ticket_type_id: Number(typeId),
      ticket_priority: priority,
      ticket_content: content.trim(),
      service_id: serviceId !== NO_SERVICE ? Number(serviceId) : undefined,
      ticket_product_name: productName.trim() || undefined,
    });
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent size="lg">
        <DialogHeader>
          <DialogTitle>New support ticket</DialogTitle>
          <DialogDescription>
            Tell us what you need help with and our team will follow up.
          </DialogDescription>
        </DialogHeader>
        <DialogBody>
          <form
            id="create-ticket-form"
            onSubmit={(e) => {
              e.preventDefault();
              handleSubmit();
            }}
            style={{ display: 'grid', gap: 'var(--vn-space-4)' }}
          >
            <Field label="Type" required error={errors.type}>
              {(props) => (
                <Select value={typeId} onValueChange={setTypeId}>
                  <SelectTrigger {...props} invalid={!!errors.type} disabled={typesLoading}>
                    <SelectValue placeholder={typesLoading ? 'Loading…' : 'Select a type'} />
                  </SelectTrigger>
                  <SelectContent>
                    {ticketTypes.map((t) => (
                      <SelectItem key={t.ticket_type_id} value={String(t.ticket_type_id)}>
                        {ticketTypeLabel(t)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </Field>

            <Field label="Priority">
              {() => (
                <SegmentedControl
                  aria-label="Priority"
                  items={PRIORITY_ITEMS}
                  value={priority}
                  onValueChange={(v) => setPriority(v as TicketPriority)}
                />
              )}
            </Field>

            <Field
              label="Related service"
              description="Link this ticket to one of your services (optional)."
            >
              {(props) => (
                <Select value={serviceId} onValueChange={setServiceId}>
                  <SelectTrigger {...props} disabled={servicesLoading}>
                    <SelectValue placeholder={servicesLoading ? 'Loading…' : 'No specific service'} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={NO_SERVICE}>No specific service</SelectItem>
                    {services.map((s) => (
                      <SelectItem key={s.service_id} value={String(s.service_id)}>
                        {s.service_name}
                        {s.product_group_name ? ` · ${s.product_group_name}` : ''}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </Field>

            <Field
              label="Product"
              description="The product or service name, if it isn't listed above (optional)."
            >
              {(props) => (
                <Input
                  {...props}
                  placeholder="e.g. CDN, WAF, Cloud Server…"
                  value={productName}
                  onChange={(e) => setProductName(e.target.value)}
                />
              )}
            </Field>

            <Field label="Description" required error={errors.content}>
              {(props) => (
                <Textarea
                  {...props}
                  rows={5}
                  placeholder="Describe the issue, when it started, and any impact…"
                  value={content}
                  invalid={!!errors.content}
                  onChange={(e) => setContent(e.target.value)}
                />
              )}
            </Field>
          </form>
        </DialogBody>
        <DialogFooter>
          <Button variant="secondary" onClick={() => handleOpenChange(false)}>
            Cancel
          </Button>
          <Button
            type="submit"
            form="create-ticket-form"
            disabled={create.isPending}
          >
            {create.isPending ? 'Creating…' : 'Create ticket'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
